import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { deleteVersionsPath, previewRows } from '../../lib/asset-versions.js';
import { authorLabel } from '../../lib/authors.js';
import { BaseComponent } from '../../lib/base-component.js';
import { downloadAssetFile } from '../../lib/download.js';
import { router } from '../../lib/router.js';
import '../shared/app-pins-panel.js';
import '../shared/asset-versions-table.js';
import '../shared/author-editor.js';
import '../shared/image-lightbox.js';
import '../commissions/tile-brush-previews.js';
import './tile-brush-draw-preview.js';
import { defaultSelectedVersions } from '../../lib/miniature-versions.js';
import { currentTileBrushTiles } from '../../lib/tile-brush-files.js';
import { uploadFile } from '../../lib/upload.js';

class AssetTypeView extends BaseComponent {
    static properties = {
        assetType: { type: String, attribute: 'asset-type' },
        basePath: { type: String, attribute: 'base-path' },
        _assets: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _searchQuery: { type: String, state: true },
        _authorQuery: { type: String, state: true },
        _page: { type: Number, state: true },
        _total: { type: Number, state: true },
        _hasMore: { type: Boolean, state: true },
        _view: { type: String, state: true },
        _selectedAsset: { type: Object, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _versionsBusy: { type: Boolean, state: true },
        _confirmDelete: { type: Boolean, state: true },
        _availableSources: { type: Array, state: true },
        _newSource: { type: String, state: true },
        _imageDimensions: { type: Object, state: true },
        _validDPIs: { type: Array, state: true },
        _selectedDPI: { type: Number, state: true },
        _imageError: { type: String, state: true },
        _dragOver: { type: Boolean, state: true },
        _imagePreview: { type: String, state: true },
        _lightboxSrc: { type: String, state: true },
        _tileBrushUploadId: { type: String, state: true },
        _tileBrushLayers: { type: Array, state: true },
        _tileBrushExtracting: { type: Boolean, state: true },
        _downloading: { type: Boolean, state: true },
        // Every version of the asset (issue #217), and the per-source image
        // selector images use, mirroring the miniatures UI.
        _versions: { type: Array, state: true },
        _selectedVersions: { type: Object, state: true },
        _versionDownloading: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .asset-thumb-sm { width: 48px; height: 32px; border-radius: 4px; object-fit: contain; background: #f0f0f0; }
        .asset-thumb-sm-square { width: 128px; height: 128px; border-radius: 4px; object-fit: contain; background: #f0f0f0; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
        .detail-header-icon {
            width: 120px; height: 80px; border-radius: 8px; background: #f0f0f0;
            display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .detail-header-icon img { width: 100%; height: 100%; object-fit: contain; }
        .detail-header-info h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .detail-header-info p { margin: 0; color: #666; font-size: 0.9rem; }
        .detail-section { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1.25rem; }
        .detail-section h6 { font-weight: 600; margin-bottom: 1rem; }
        .prop-row { display: flex; padding: 0.5rem 0; border-bottom: 1px solid #f0f0f0; }
        .prop-row:last-child { border-bottom: none; }
        .prop-label { width: 120px; font-weight: 500; color: #666; font-size: 0.85rem; flex-shrink: 0; }
        .prop-value { flex: 1; font-size: 0.9rem; }
        .form-panel { max-width: 500px; }
        .drop-zone {
            border: 2px dashed #ccc; border-radius: 8px; padding: 2rem; text-align: center;
            cursor: pointer; transition: border-color 0.15s, background-color 0.15s; position: relative;
        }
        .drop-zone:hover, .drop-zone.drag-over { border-color: #0d6efd; background-color: #f0f6ff; }
        .drop-zone input[type="file"] { position: absolute; width: 0; height: 0; opacity: 0; }
        .drop-zone-preview { max-width: 100%; max-height: 200px; object-fit: contain; border-radius: 4px; margin-bottom: 0.5rem; }
    `;

    constructor() {
        super();
        this.assetType = '';
        this.basePath = '';
        this._assets = [];
        this._loading = true;
        this._error = null;
        this._searchQuery = '';
        this._authorQuery = '';
        this._page = 0;
        this._total = 0;
        this._hasMore = false;
        this._view = 'list';
        this._selectedAsset = null;
        this._formData = {};
        this._saving = false;
        this._confirmDelete = false;
        this._availableSources = [];
        this._newSource = '';
        this._imageDimensions = null;
        this._validDPIs = [];
        this._selectedDPI = null;
        this._imageError = null;
        this._dragOver = false;
        this._imagePreview = null;
        this._lightboxSrc = '';
        this._tileBrushUploadId = '';
        this._tileBrushLayers = [];
        this._tileBrushExtracting = false;
        this._versions = [];
        this._selectedVersions = {};
        this._versionDownloading = null;
    }

    get _displayName() {
        switch (this.assetType) {
            case 'image':
                return 'Images';
            case 'title-screen':
                return 'Title Screens';
            case 'tactical-map':
                return 'Tactical Maps';
            case 'mapmaking-tile-brush':
                return 'Tile Brushes';
            default:
                return this.assetType;
        }
    }

    get _singularName() {
        switch (this.assetType) {
            case 'image':
                return 'Image';
            case 'title-screen':
                return 'Title Screen';
            case 'tactical-map':
                return 'Tactical Map';
            case 'mapmaking-tile-brush':
                return 'Tile Brush';
            default:
                return this.assetType;
        }
    }

    get _canCreate() {
        if (!this._formData.file || !this._formData.slug) return false;
        if (this._imageError) return false;
        if (this.assetType === 'tactical-map' && !this._selectedDPI) return false;
        if (this.assetType === 'title-screen' && !this._imageDimensions) return false;
        return true;
    }

    get _thumbClass() {
        return this.assetType === 'tactical-map' ? 'asset-thumb-sm-square' : 'asset-thumb-sm';
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe(() => this._syncFromRoute());
        this._syncFromRoute();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _syncFromRoute() {
        const path = router.getPath();
        if (path === `${this.basePath}/new`) {
            await this._loadSources();
            const defaultSource = this._availableSources.find((s) => s.isDefault);
            this._formData = { slug: '', source: defaultSource?.slug || '', file: null };
            this._imageDimensions = null;
            this._validDPIs = [];
            this._selectedDPI = null;
            this._imageError = null;
            if (this._imagePreview) URL.revokeObjectURL(this._imagePreview);
            this._imagePreview = null;
            this._view = 'create';
            this._error = null;
        } else if (path.match(new RegExp(`^${this.basePath}/[^/]+$`)) && router.params.id) {
            await this._openDetail({ id: router.params.id });
        } else {
            this._view = 'list';
            this._selectedAsset = null;
            this._loadAssets();
        }
    }

    async _loadAssets() {
        this._loading = true;
        this._error = null;
        try {
            const params = new URLSearchParams();
            params.set('page', String(this._page));
            params.set('pageSize', '50');
            params.set('type', this.assetType);
            if (this._searchQuery) params.set('query', this._searchQuery);
            if (this._authorQuery) params.set('author', this._authorQuery);
            const data = await api.get(`/api/assets?${params}`);
            this._assets = data.assets || [];
            this._total = data.total || 0;
            this._hasMore = data.hasMore || false;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _openDetail(asset) {
        this._error = null;
        try {
            this._selectedAsset = await api.get(`/api/assets/${asset.id}`);
            await this._loadSources();
            // Every asset lists its versions; images also use the per-source
            // version selector, and tile brushes feed their current version's
            // pieces to the draw-preview.
            await this._loadVersions(asset.id);
            this._resetVersionSelection();
            this._view = 'detail';
        } catch (err) {
            this._error = err.message;
        }
    }

    // Persist the tile-brush straight-seam-connector metadata. Updating
    // _selectedAsset re-renders the checkbox and feeds the new value into the
    // draw-preview so its render reflects the saved policy immediately.
    async _toggleSeamConnector(e) {
        const useSeamConnector = e.target.checked;
        this._saving = true;
        this._error = null;
        try {
            await api.put(`/api/assets/${this._selectedAsset.id}`, {
                use_straight_seam_connector: useSeamConnector,
            });
            this._selectedAsset = {
                ...this._selectedAsset,
                use_straight_seam_connector: useSeamConnector,
            };
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    // Publish the asset to the freelancer catalog, or take it out (issue #205).
    async _toggleCatalog(e) {
        const inCatalog = e.target.checked;
        this._saving = true;
        this._error = null;
        try {
            const updated = await api.put(`/api/assets/${this._selectedAsset.id}`, {
                in_catalog: inCatalog,
            });
            this._selectedAsset = {
                ...this._selectedAsset,
                in_catalog: updated.in_catalog,
                catalog_published_at: updated.catalog_published_at,
            };
        } catch (err) {
            e.target.checked = !inCatalog;
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    // Delete one version of a source, or the whole source (issue #199), then
    // reload the asset: its current file, versions and what apps serve may all
    // change.
    async _deleteVersions(e) {
        const { source, version } = e.detail;
        this._versionsBusy = true;
        this._error = null;
        try {
            await api.delete(deleteVersionsPath(this._selectedAsset.id, source, version));
            await this._openDetail(this._selectedAsset);
            this._pinsPanel()?.reload();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionsBusy = false;
        }
    }

    async _setCurrent(e) {
        this._versionsBusy = true;
        this._error = null;
        try {
            await api.put(
                `/api/assets/${this._selectedAsset.id}/versions/${e.detail.versionId}/set-current`,
            );
            await this._openDetail(this._selectedAsset);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionsBusy = false;
        }
    }

    // Credit a version to its author (issue #215), or clear it, and show the
    // versions the API answers.
    async _setAuthor(e) {
        const { versionId, body } = e.detail;
        this._versionsBusy = true;
        this._error = null;
        try {
            const data = await api.put(
                `/api/assets/${this._selectedAsset.id}/versions/${versionId}/author`,
                body,
            );
            this._versions = data.versions || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionsBusy = false;
        }
    }

    async _loadVersions(assetID) {
        try {
            const data = await api.get(`/api/assets/${assetID}/versions`);
            this._versions = data.versions || [];
        } catch {
            this._versions = [];
        }
    }

    _pinsPanel() {
        return /** @type {any} */ (this.shadowRoot?.querySelector('app-pins-panel'));
    }

    // Make every app serve one version (the versions table's "Pin for every
    // app").
    async _pinAll(e) {
        await this._pinsPanel()?.setPin(null, {
            source: e.detail.source,
            version: e.detail.version,
        });
    }

    async _downloadInput(e) {
        this._error = null;
        try {
            await downloadAssetFile(this._selectedAsset.id, {
                source: e.detail.source,
                version: e.detail.version,
                kind: 'input',
            });
        } catch (err) {
            this._error = err.message;
        }
    }

    _resetVersionSelection() {
        this._selectedVersions = defaultSelectedVersions(
            this._sourceGroups(previewRows(this._versions)),
        );
    }

    _sourceGroups(files) {
        const groups = {};
        for (const f of files) {
            if (!groups[f.source])
                groups[f.source] = { source: f.source, files: [], current: null };
            groups[f.source].files.push(f);
            if (f.is_current) groups[f.source].current = f;
        }
        for (const g of Object.values(groups)) {
            g.files.sort((a, b) => b.version - a.version);
            if (!g.current) g.current = g.files[0];
        }
        return Object.values(groups);
    }

    async _downloadVersion(source, file) {
        this._versionDownloading = source;
        this._error = null;
        try {
            await downloadAssetFile(this._selectedAsset.id, { source, version: file.version });
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionDownloading = null;
        }
    }

    // The extracted brush tiles as a labeled grid — the tile-brush counterpart
    // to the per-source version list. Reads _versions, so it updates in place
    // after Replace PSD.
    _renderTileSheet() {
        const tiles = this._tileBrushTiles();
        if (tiles.length === 0) {
            return html`<p class="text-muted">No tiles extracted yet.</p>`;
        }
        return html`
            <div style="display: flex; flex-wrap: wrap; gap: 0.75rem;">
                ${tiles.map(
                    (t) => html`
                    <figure style="margin: 0; width: 120px; text-align: center;">
                        <img src="${t.file_url}" alt="${t.layer_name}"
                            style="width: 100%; aspect-ratio: 1; object-fit: contain; border: 1px solid #dee2e6; border-radius: 4px; background: repeating-conic-gradient(#fff 0% 25%, #eef 0% 50%) 50% / 12px 12px;">
                        <figcaption style="font-family: ui-monospace, monospace; font-size: 0.7rem; color: #495057; margin-top: 0.25rem; word-break: break-all;">${t.layer_name}</figcaption>
                    </figure>
                `,
                )}
            </div>
        `;
    }

    // Per-source version selector + download controls, mirroring the
    // miniatures detail page.
    _renderImageVersions() {
        const rows = previewRows(this._versions);
        if (rows.length === 0) {
            return html`<p class="text-muted">No image uploaded.</p>`;
        }
        return html`
            <div style="display: flex; flex-wrap: wrap; gap: 1.5rem; margin-bottom: 1rem;">
                ${this._sourceGroups(rows).map(({ source, files, current }) => {
                    const selectedVersion = this._selectedVersions[source] ?? current.version;
                    const selected = files.find((f) => f.version === selectedVersion) ?? current;
                    return html`
                    <div style="text-align: center;">
                        <img src="${selected.image_url}" style="width: 120px; height: 120px; object-fit: contain; background: #f8f9fa; border-radius: 6px; border: 2px solid #dee2e6;" alt="">
                        <div style="font-size: 0.75rem; color: #666; margin-top: 0.25rem;">${source} (v${selected.version})</div>
                        <div style="margin-top: 0.5rem; display: flex; align-items: center; gap: 0.25rem; justify-content: center;">
                            <span style="font-size: 0.7rem; color: #999;">version:</span>
                            <select class="form-select form-select-sm" style="width: auto; font-size: 0.75rem; padding: 0.15rem 1.5rem 0.15rem 0.4rem;"
                                @change="${(e) => (this._selectedVersions = { ...this._selectedVersions, [source]: parseInt(e.target.value) })}">
                                ${files.map(
                                    (f) => html`
                                    <option value="${f.version}" ?selected="${selectedVersion === f.version}">v${f.version}</option>
                                `,
                                )}
                            </select>
                        </div>
                        <div style="margin-top: 0.5rem; display: flex; align-items: center; gap: 0.25rem; justify-content: center;">
                            <button class="btn btn-outline-secondary btn-sm" style="font-size: 0.7rem; padding: 0.1rem 0.4rem;"
                                ?disabled="${this._versionDownloading === source}"
                                @click="${() => this._downloadVersion(source, selected)}">
                                <i class="bi bi-download"></i> ${this._versionDownloading === source ? 'Downloading...' : 'Download'}
                            </button>
                        </div>
                    </div>
                `;
                })}
            </div>
        `;
    }

    _navigateToDetail(asset) {
        router.navigate(`${this.basePath}/${asset.id}`);
    }

    async _loadSources() {
        try {
            const data = await api.get('/api/sources');
            this._availableSources = data.sources || [];
            const defaultSource = this._availableSources.find((s) => s.isDefault);
            if (defaultSource) this._newSource = defaultSource.slug;
        } catch {
            this._availableSources = [];
        }
    }

    _backToList() {
        router.navigate(this.basePath);
    }

    _startCreate() {
        router.navigate(`${this.basePath}/new`);
    }

    async _submitCreate(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            // For tile brushes we already uploaded the PSD during the
            // preview-extraction step on drop; reuse that upload_id so
            // we don't double-upload (and re-extract a second time).
            let uploadId = this._tileBrushUploadId;
            if (!uploadId) {
                const upload = await uploadFile(this._formData.file);
                uploadId = upload.upload_id;
            }
            const form = new FormData();
            form.append('slug', this._formData.slug);
            form.append('asset_type', this.assetType);
            form.append('source', this._formData.source);
            form.append('upload_id', uploadId);
            if (this._selectedDPI) {
                form.append('grid_dpi', String(this._selectedDPI));
            }
            const created = await api.upload('/api/assets', form);
            router.navigate(`${this.basePath}/${created.id}`);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _deleteAsset() {
        if (!this._selectedAsset) return;
        this._saving = true;
        this._error = null;
        try {
            await api.delete(`/api/assets/${this._selectedAsset.id}`);
            router.navigate(this.basePath);
        } catch (err) {
            this._error = err.message;
            this._confirmDelete = false;
        } finally {
            this._saving = false;
        }
    }

    async _downloadCurrent() {
        const file = this._selectedAsset?.current_file;
        if (!file?.url) return;
        this._downloading = true;
        this._error = null;
        try {
            await downloadAssetFile(this._selectedAsset.id, {
                source: file.source,
                version: file.version,
            });
        } catch (err) {
            this._error = err.message;
        } finally {
            this._downloading = false;
        }
    }

    async _uploadNewImage(e) {
        const file = e.target.files[0];
        if (!file || !this._newSource) return;
        this._saving = true;
        this._error = null;
        try {
            // Route through the upload pipeline so the worker processes (and
            // sanitizes SVG) before we record the version — same contract as
            // asset creation and the miniatures uploader.
            const upload = await uploadFile(file);
            const form = new FormData();
            form.append('upload_id', upload.upload_id);
            form.append('source', this._newSource);
            await api.upload(`/api/assets/${this._selectedAsset.id}/files`, form);
            this._selectedAsset = await api.get(`/api/assets/${this._selectedAsset.id}`);
            await this._loadVersions(this._selectedAsset.id);
            this._resetVersionSelection();
            this._pinsPanel()?.reload();
            this._newSource = '';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    // Upload a PSD as the next version of one of a tile brush's sources and
    // extract its tiles. A brush's versions are extracted, so this goes to the
    // dedicated re-extract endpoint (not the generic /files upload, which
    // rejects tile brushes), then refreshes the tile sheet + preview.
    async _updateTileBrushPsd(e) {
        const file = e.target.files[0];
        if (!file || !this._newSource) return;
        this._saving = true;
        this._error = null;
        try {
            const id = this._selectedAsset.id;
            const upload = await uploadFile(file);
            const data = await api.put(`/api/assets/${id}/tile-brush`, {
                upload_id: upload.upload_id,
                source: this._newSource,
            });
            // Refresh both the versions (tile sheet + preview) and the asset
            // record (its current_file drives the header thumbnail) so the page
            // updates in place without a manual reload.
            this._versions = data.versions || [];
            this._selectedAsset = await api.get(`/api/assets/${id}`);
            this._pinsPanel()?.reload();
        } catch (err) {
            this._error = err.message || 'PSD re-extraction failed';
        } finally {
            this._saving = false;
            e.target.value = '';
        }
    }

    _clearFile() {
        this._processFile(null);
        const input = /** @type {HTMLInputElement | null} */ (
            this.shadowRoot.querySelector('#create-file-input')
        );
        if (input) input.value = '';
    }

    _handleFileChange(e) {
        this._processFile(e.target.files[0]);
    }

    _handleDrop(e) {
        e.preventDefault();
        this._dragOver = false;
        const file = e.dataTransfer.files[0];
        if (!file) return;
        // Tile-brush direct-create accepts PSDs (with extension fallback —
        // some browsers don't tag .psd with the standard mime). Everything
        // else expects an image or webm.
        const isPSD = file.type === 'image/vnd.adobe.photoshop' || /\.psd$/i.test(file.name);
        if (this.assetType === 'mapmaking-tile-brush' && isPSD) {
            this._processFile(file);
            return;
        }
        // SVGs are sometimes reported as text/xml or an empty type by the
        // browser, so fall back to the extension.
        if (
            file.type.startsWith('image/') ||
            file.type === 'video/webm' ||
            /\.svg$/i.test(file.name)
        ) {
            this._processFile(file);
        }
    }

    _processFile(file) {
        if (this._imagePreview) URL.revokeObjectURL(this._imagePreview);
        this._formData = { ...this._formData, file: file || null };
        this._imageDimensions = null;
        this._validDPIs = [];
        this._selectedDPI = null;
        this._imageError = null;
        this._imagePreview = null;
        this._tileBrushUploadId = '';
        this._tileBrushLayers = [];
        this._tileBrushExtracting = false;

        if (!file) return;

        this._imagePreview = URL.createObjectURL(file);

        if (this.assetType === 'tactical-map') {
            this._analyzeMapImage(file);
        } else if (this.assetType === 'title-screen') {
            this._analyzeTitleScreenImage(file);
        } else if (this.assetType === 'mapmaking-tile-brush') {
            this._extractTileBrushPreview(file);
        }
    }

    async _extractTileBrushPreview(file) {
        this._tileBrushExtracting = true;
        this._imageError = null;
        try {
            const upload = await uploadFile(file);
            this._tileBrushUploadId = upload.upload_id;
            const resp = await api.post('/api/tile-brushes/preview-extraction', {
                upload_id: upload.upload_id,
            });
            this._tileBrushLayers = resp.layers || [];
        } catch (err) {
            this._imageError = err.message || 'PSD layer extraction failed';
            this._tileBrushUploadId = '';
            this._tileBrushLayers = [];
        } finally {
            this._tileBrushExtracting = false;
        }
    }

    _analyzeMapImage(_file) {
        const VALID_DPIS = [70, 100, 140, 200];
        const img = new Image();
        img.onload = () => {
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            this._imageDimensions = { width: w, height: h };

            const matches = VALID_DPIS.filter((dpi) => w % dpi === 0 && h % dpi === 0);
            if (matches.length === 0) {
                this._imageError = `Image is ${w}x${h} which is not evenly divisible by any supported grid DPI (${VALID_DPIS.join(', ')}).`;
                this._validDPIs = [];
            } else if (matches.length === 1) {
                this._validDPIs = matches;
                this._selectedDPI = matches[0];
            } else {
                this._validDPIs = matches;
                this._selectedDPI = null;
            }
        };
        img.onerror = () => {
            this._imageError = 'Could not read image dimensions.';
        };
        img.src = this._imagePreview;
    }

    _analyzeTitleScreenImage(_file) {
        const img = new Image();
        img.onload = () => {
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            this._imageDimensions = { width: w, height: h };
            if (w < 1920 || h < 1080) {
                this._imageError = `Image is ${w}x${h} but title screens must be at least 1920x1080.`;
            } else if (w * 9 !== h * 16) {
                this._imageError = `Image is ${w}x${h} but title screens must be 16:9 aspect ratio.`;
            }
        };
        img.onerror = () => {
            this._imageError = 'Could not read image dimensions.';
        };
        img.src = this._imagePreview;
    }

    _handleSearch(e) {
        this._searchQuery = e.target.value;
        this._page = 0;
        this._loadAssets();
    }
    _handleAuthorSearch(e) {
        this._authorQuery = e.target.value;
        this._page = 0;
        this._loadAssets();
    }
    _handlePrevPage() {
        if (this._page > 0) {
            this._page--;
            this._loadAssets();
        }
    }
    _handleNextPage() {
        if (this._hasMore) {
            this._page++;
            this._loadAssets();
        }
    }

    _renderList() {
        return html`
            <div class="page-header">
                <h4>${this._displayName}</h4>
                <button class="btn btn-primary btn-sm" @click="${this._startCreate}">+ Create ${this._singularName}</button>
            </div>
            <p class="page-subtitle">Manage ${this._displayName.toLowerCase()} assets. ${this._total} total.</p>

            <div class="d-flex gap-2 mb-3">
                <input type="text" class="form-control form-control-sm" placeholder="Search..." style="max-width: 250px;"
                    .value="${this._searchQuery}" @input="${this._handleSearch}">
                <input type="text" class="form-control form-control-sm" placeholder="Author..." style="max-width: 200px;"
                    aria-label="Filter by author" .value="${this._authorQuery}" @input="${this._handleAuthorSearch}">
            </div>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th style="width: 56px;"></th>
                            <th>Slug</th>
                            <th>Source</th>
                            <th>Author</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._assets.map(
                            (a) => html`
                            <tr style="cursor: pointer;" @click="${() => this._navigateToDetail(a)}">
                                <td>
                                    ${
                                        a.current_file?.url
                                            ? html`<img src="${api.getImageUrl(a.current_file.url)}" class="${this._thumbClass}" alt="">`
                                            : html`<div class="${this._thumbClass}"></div>`
                                    }
                                </td>
                                <td>
                                    <span style="color: #0d6efd; font-weight: 500;">${a.slug}</span>
                                    ${a.in_catalog ? nothing : html`<span class="badge bg-danger text-white ms-2" style="font-size: 0.7rem;"><i class="bi bi-exclamation-triangle-fill me-1"></i>Unpublished</span>`}
                                </td>
                                <td class="text-muted">${a.current_file?.source || '-'}</td>
                                <td class="text-muted">${authorLabel(a.current_file?.author) || '-'}</td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>

                ${this._assets.length === 0 ? html`<p class="text-muted text-center py-3">No ${this._displayName.toLowerCase()} found.</p>` : nothing}

                <div class="d-flex justify-content-between align-items-center">
                    <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._page === 0}" @click="${this._handlePrevPage}">Previous</button>
                    <span class="text-muted small">Page ${this._page + 1}</span>
                    <button class="btn btn-sm btn-outline-secondary" ?disabled="${!this._hasMore}" @click="${this._handleNextPage}">Next</button>
                </div>
            `
            }
        `;
    }

    /**
     * This tile brush's tiles at its newest version, shaped for the
     * wall-renderer preview: { layer_name, file_url } per piece.
     */
    _tileBrushTiles() {
        return currentTileBrushTiles(this._versions);
    }

    _renderDetail() {
        const a = this._selectedAsset;
        if (!a) return nothing;

        const file = a.current_file;
        const imageUrl = file?.url ? api.getImageUrl(file.url) : null;

        return html`
            <a class="back-link" href="${this.basePath}">
                <i class="bi bi-arrow-left"></i> Back to ${this._displayName}
            </a>

            <div class="detail-header">
                <div class="detail-header-icon">
                    ${imageUrl ? html`<img src="${imageUrl}" alt="">` : html`<i class="bi bi-image" style="font-size: 1.5rem; color: #999;"></i>`}
                </div>
                <div class="detail-header-info" style="flex: 1;">
                    <h4>${a.slug}</h4>
                    <p>${this._singularName}</p>
                </div>
                <div>
                    ${
                        imageUrl && !this._confirmDelete && this.assetType !== 'image'
                            ? html`
                        <button type="button" class="btn btn-outline-secondary btn-sm me-1" ?disabled="${this._downloading}" @click="${this._downloadCurrent}">
                            <i class="bi bi-download"></i> ${this._downloading ? 'Downloading...' : 'Download'}
                        </button>
                    `
                            : nothing
                    }
                    ${
                        this._confirmDelete
                            ? html`
                        <span class="text-muted small me-2">Delete this ${this._singularName.toLowerCase()}?</span>
                        <button type="button" class="btn btn-danger btn-sm" ?disabled="${this._saving}" @click="${this._deleteAsset}">
                            Confirm
                        </button>
                        <button type="button" class="btn btn-outline-secondary btn-sm ms-1" ?disabled="${this._saving}" @click="${() => (this._confirmDelete = false)}">
                            Cancel
                        </button>
                    `
                            : html`
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="${() => (this._confirmDelete = true)}">
                            <i class="bi bi-trash"></i> Delete
                        </button>
                    `
                    }
                </div>
            </div>

            <div class="detail-section">
                <h6>Properties</h6>
                <div class="prop-row">
                    <div class="prop-label">Slug</div>
                    <div class="prop-value"><code>${a.slug}</code></div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">Type</div>
                    <div class="prop-value">${a.asset_type}</div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">ID</div>
                    <div class="prop-value"><code class="small">${a.id}</code></div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">Published</div>
                    <div class="prop-value">
                        <div class="form-check form-switch mb-0">
                            <input class="form-check-input" type="checkbox" role="switch" id="in-catalog"
                                .checked="${Boolean(a.in_catalog)}"
                                ?disabled="${this._saving}"
                                @change="${this._toggleCatalog}">
                            <label class="form-check-label" for="in-catalog">
                                ${a.in_catalog ? 'Published' : 'Not published'}
                                <span class="text-muted small d-block">
                                    ${
                                        this.assetType === 'mapmaking-tile-brush'
                                            ? 'Tile brushes don’t show in the Asset Catalog yet, even when published: they have no d3 image to show. Apps will also use it to leave out art that isn’t published.'
                                            : 'Freelancers can browse and download every version of its d3 image in the Asset Catalog when it’s published. Apps will also use it to leave out art that isn’t published.'
                                    }
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div class="detail-section">
                <h6>${this.assetType === 'image' ? 'Images' : this.assetType === 'mapmaking-tile-brush' ? 'Tile sheet' : 'Image'}</h6>
                ${
                    this.assetType === 'image'
                        ? this._renderImageVersions()
                        : this.assetType === 'mapmaking-tile-brush'
                          ? this._renderTileSheet()
                          : imageUrl
                            ? html`
                    <div style="margin-bottom: 1rem;">
                        <img src="${imageUrl}" style="max-width: 100%; max-height: 400px; object-fit: contain; background: #f8f9fa; border-radius: 6px; border: 2px solid #dee2e6;" alt="">
                        ${file ? html`<div style="font-size: 0.75rem; color: #666; margin-top: 0.25rem;">${file.source} (v${file.version})${file.width ? ` — ${file.width}x${file.height}` : ''}</div>` : nothing}
                    </div>
                `
                            : html`<p class="text-muted">No image uploaded.</p>`
                }
                ${
                    this.assetType !== 'mapmaking-tile-brush' && this._availableSources.length > 0
                        ? html`
                    <div class="mt-2">
                        <label class="form-label small text-muted">Upload image</label>
                        <div class="d-flex gap-2 align-items-center" style="max-width: 400px;">
                            <select class="form-select form-select-sm" style="max-width: 200px;"
                                @change="${(e) => (this._newSource = e.target.value)}">
                                <option value="">Select source...</option>
                                ${this._availableSources.map(
                                    (s) => html`
                                    <option value="${s.slug}" ?selected="${this._newSource === s.slug}">${s.label}</option>
                                `,
                                )}
                            </select>
                            <input type="file" class="form-control form-control-sm" accept="image/*"
                                ?disabled="${this._saving || !this._newSource}"
                                @change="${this._uploadNewImage}">
                        </div>
                    </div>
                `
                        : nothing
                }
            </div>

            <div class="detail-section">
                <h6>Versions</h6>
                <asset-versions-table .versions="${this._versions || []}" ?busy="${this._versionsBusy}" can-pin
                    @delete-versions="${this._deleteVersions}" @set-current="${this._setCurrent}"
                    @set-author="${this._setAuthor}" @pin-all="${this._pinAll}"
                    @download-input="${this._downloadInput}"></asset-versions-table>
            </div>

            <div class="detail-section">
                <h6>Served by apps</h6>
                <p class="text-muted small mb-2">
                    Each app serves what its pin names: a version of a source, or a source's latest. An app with no pin serves d3's latest.
                </p>
                <app-pins-panel asset-id="${a.id}" .versions="${this._versions || []}"></app-pins-panel>
            </div>

            ${
                this.assetType === 'mapmaking-tile-brush'
                    ? html`
                <div class="detail-section">
                    <h6>Brush settings</h6>
                    <p class="text-muted small mb-2">
                        Stored on the brush and read by the renderer — these control how the brush draws everywhere it's used, not just here.
                    </p>
                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" id="use-straight-seam-connector"
                            .checked="${a.use_straight_seam_connector !== false}"
                            ?disabled="${this._saving}"
                            @change="${this._toggleSeamConnector}">
                        <label class="form-check-label" for="use-straight-seam-connector">
                            Straight-seam connector
                            <span class="text-muted small d-block">
                                Overlay the seam connector on straight-to-straight seams so a run reads as one continuous piece.
                                Turn off for a segmented/block brush so straights abut as hard edges.
                            </span>
                        </label>
                    </div>
                    <div class="mt-3">
                        <label class="form-label small text-muted">Upload a new version</label>
                        <div class="d-flex gap-2 align-items-center" style="max-width: 400px;">
                            <select class="form-select form-select-sm" style="max-width: 200px;"
                                @change="${(e) => (this._newSource = e.target.value)}">
                                <option value="">Select source...</option>
                                ${this._availableSources.map(
                                    (s) => html`
                                    <option value="${s.slug}" ?selected="${this._newSource === s.slug}">${s.label}</option>
                                `,
                                )}
                            </select>
                            <input type="file" class="form-control form-control-sm" accept=".psd"
                                ?disabled="${this._saving || !this._newSource}"
                                @change="${this._updateTileBrushPsd}">
                        </div>
                        <p class="text-muted small mt-1 mb-0">Upload a PSD as the next version of a source and extract its tiles. Earlier versions are kept.</p>
                        ${this._error ? html`<div class="text-danger small mt-1">${this._error}</div>` : nothing}
                    </div>
                </div>
            `
                    : nothing
            }

            ${
                this.assetType === 'mapmaking-tile-brush' && this._tileBrushTiles().length > 0
                    ? html`
                <div class="detail-section">
                    <tile-brush-draw-preview
                        .layers="${this._tileBrushTiles()}"
                        .useSeamConnector="${a.use_straight_seam_connector}"></tile-brush-draw-preview>
                </div>
            `
                    : nothing
            }
        `;
    }

    _renderCreate() {
        return html`
            <a class="back-link" href="${this.basePath}">
                <i class="bi bi-arrow-left"></i> Back to ${this._displayName}
            </a>
            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Create ${this._singularName}</h4>
            <div class="form-panel">
                <form @submit="${this._submitCreate}">
                    <div class="mb-3">
                        <label class="form-label">Slug *</label>
                        <input type="text" class="form-control" required placeholder="e.g. forest-clearing"
                            .value="${this._formData.slug}"
                            @input="${(e) => (this._formData = { ...this._formData, slug: e.target.value })}">
                    </div>
                    ${
                        this._availableSources.length > 0
                            ? html`
                        <div class="mb-3">
                            <label class="form-label">Source *</label>
                            <select class="form-select" required
                                @change="${(e) => (this._formData = { ...this._formData, source: e.target.value })}">
                                ${this._availableSources.map(
                                    (s) => html`
                                    <option value="${s.slug}" ?selected="${this._formData.source === s.slug}">${s.label}</option>
                                `,
                                )}
                            </select>
                        </div>
                    `
                            : ''
                    }
                    <div class="mb-3">
                        <label class="form-label">Image *</label>
                        <div class="drop-zone ${this._dragOver ? 'drag-over' : ''}"
                             @drop="${this._handleDrop}"
                             @dragover="${(e) => {
                                 e.preventDefault();
                                 this._dragOver = true;
                             }}"
                             @dragleave="${() => (this._dragOver = false)}"
                             @click="${() => /** @type {HTMLElement} */ (this.shadowRoot.querySelector('#create-file-input')).click()}">
                            <input type="file" id="create-file-input"
                                accept="${this.assetType === 'mapmaking-tile-brush' ? '.psd' : this.assetType === 'image' ? 'image/png,image/webp,image/jpeg,image/svg+xml,.svg' : 'image/png,image/jpeg,image/webp,video/webm'}"
                                @change="${this._handleFileChange}">
                            ${
                                this._imagePreview
                                    ? html`
                                ${
                                    this._formData.file?.type === 'image/vnd.adobe.photoshop' ||
                                    /\.psd$/i.test(this._formData.file?.name || '')
                                        ? html`
                                        <div class="d-flex flex-column align-items-center justify-content-center" style="height: 200px;">
                                            <i class="bi bi-file-earmark-richtext" style="font-size: 3rem; color: #6c757d;"></i>
                                            <div class="text-muted small mt-2">PSD — preview after extraction</div>
                                        </div>
                                    `
                                        : this._formData.file?.type === 'video/webm'
                                          ? html`<video class="drop-zone-preview" src="${this._imagePreview}" autoplay loop muted playsinline></video>`
                                          : html`<img class="drop-zone-preview" src="${this._imagePreview}" alt="Preview">`
                                }
                                <div class="d-flex align-items-center justify-content-center gap-2 mt-1">
                                    <span class="text-muted small">${this._formData.file?.name}</span>
                                    <button type="button" class="btn btn-link btn-sm p-0 text-danger" style="font-size: 0.85rem; line-height: 1;"
                                        @click="${(e) => {
                                            e.stopPropagation();
                                            this._clearFile();
                                        }}">
                                        <i class="bi bi-trash"></i>
                                    </button>
                                </div>
                            `
                                    : html`
                                <i class="bi bi-cloud-arrow-up" style="font-size: 1.5rem; color: #999;"></i>
                                <p class="text-muted small mb-0 mt-1">${this.assetType === 'mapmaking-tile-brush' ? 'Drop a tile-brush PSD here or click to browse' : 'Drop image here or click to browse'}</p>
                            `
                            }
                        </div>
                        ${
                            this.assetType === 'title-screen' && !this._imageDimensions
                                ? html`
                            <div class="form-text">16:9 aspect ratio, minimum 1920x1080. Recommended 3840x2160.</div>
                        `
                                : ''
                        }
                        ${
                            this.assetType === 'tactical-map' && !this._imageDimensions
                                ? html`
                            <div class="form-text">Dimensions must be evenly divisible by a supported grid DPI (70, 100, 140, or 200).</div>
                        `
                                : ''
                        }
                        ${
                            this.assetType === 'mapmaking-tile-brush' && this._tileBrushExtracting
                                ? html`
                            <div class="text-muted small mt-2">
                                <span class="spinner-border spinner-border-sm me-1"></span>
                                Extracting layers and rendering previews…
                            </div>
                        `
                                : ''
                        }
                        ${
                            this.assetType === 'mapmaking-tile-brush' &&
                            this._tileBrushLayers.length > 0
                                ? html`
                            <div class="mt-3">
                                <tile-brush-previews .layers="${this._tileBrushLayers}"></tile-brush-previews>
                            </div>
                        `
                                : ''
                        }
                    </div>
                    ${
                        this._imageError
                            ? html`
                        <div class="alert alert-danger small">${this._imageError}</div>
                    `
                            : ''
                    }
                    ${
                        this._imageDimensions && !this._imageError
                            ? html`
                        <div class="alert alert-success small mb-3">
                            Image: ${this._imageDimensions.width}x${this._imageDimensions.height}
                            ${
                                this.assetType === 'tactical-map' && this._selectedDPI
                                    ? html`
                                — ${this._selectedDPI}px DPI, ${this._imageDimensions.width / this._selectedDPI}x${this._imageDimensions.height / this._selectedDPI} grid
                            `
                                    : ''
                            }
                        </div>
                    `
                            : ''
                    }
                    ${
                        this._validDPIs.length > 1 && !this._selectedDPI
                            ? html`
                        <div class="mb-3">
                            <label class="form-label">Grid DPI *</label>
                            <select class="form-select" @change="${(e) => (this._selectedDPI = parseInt(e.target.value))}">
                                <option value="" selected>Select DPI...</option>
                                ${this._validDPIs.map(
                                    (dpi) => html`
                                    <option value="${dpi}">${dpi}px (${this._imageDimensions.width / dpi}x${this._imageDimensions.height / dpi} grid)</option>
                                `,
                                )}
                            </select>
                            <div class="form-text">Multiple grid sizes are possible for this image. Select the intended DPI.</div>
                        </div>
                    `
                            : ''
                    }
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving || !this._canCreate}">
                        ${this._saving ? 'Creating...' : 'Create'}
                    </button>
                </form>
            </div>
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'detail' ? this._renderDetail() : ''}
            ${this._view === 'create' ? this._renderCreate() : ''}
            <image-lightbox .src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = '')}"></image-lightbox>
        `;
    }
}

customElements.define('asset-type-view', AssetTypeView);
